import { Input } from "antd";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiPath from "../constants/apiPath";
import useRequest from "../hooks/useRequest";
const { Search } = Input;

const Section = ({ title, children }) => (
  <div className="gs-section">
    <div className="gs-title">{title}</div>
    {children}
  </div>
);

const Item = ({ primary, secondary, onClick }) => (
  <div className="gs-item" onClick={onClick}>
    <div className="primary">{primary}</div>
    {secondary && <div className="secondary">{secondary}</div>}
  </div>
);

const GlobalSearch = () => {
  const navigate = useNavigate();
  const wrapperRef = useRef(null);
  const { request } = useRequest();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // 🔹 Debounce search
  useEffect(() => {
    const delay = setTimeout(() => {
      if (query.trim()) {
        fetchResults(query);
      } else {
        setResults(null);
        setOpen(false);
      }
    }, 400);

    return () => clearTimeout(delay);
  }, [query]);

  // 🔹 Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchResults = async (value) => {
    request({
      url: `${apiPath.listGlobalSearchData}?search=${value}`,
      method: "GET",
      onSuccess: (res) => {
        setLoading(false);
        setResults(res.data);
        setOpen(true);
      },
      onError: (error) => {
        console.log(error);
      },
    });
  };

  const goTo = (path, state) => {
    setOpen(false);
    setQuery("");
    if (state) {
      navigate(path, { state });
    } else {
      navigate(path);
    }
  };

  return (
    <div className="global-search-wrapper" ref={wrapperRef}>
      <Search
        placeholder="Search users, posts, groups, tags..."
        allowClear
        loading={loading}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onSearch={(value) => fetchResults(value)} 
      />

      {open && results && (
        <div className="global-search-dropdown">
          {results.users?.length > 0 && (
            <Section title="Users">
              {results.users.map((u) => (
                <Item
                  key={u._id}
                  primary={u.name}
                  secondary={u.email}
                  onClick={() => goTo(`/admin/user-manager/view/${u._id}`)}
                />
              ))}
            </Section>
          )}

          {results.posts?.length > 0 && (
            <Section title="Posts / Videos">
              {results.posts.map((p) => (
                <Item
                  key={p?._id}
                  primary={p?.caption}
                  secondary={p?.category?.name}
                  onClick={() => goTo("/admin/post-manager", { postId: p._id })}
                />
              ))}
            </Section>
          )}

          {results.groups?.length > 0 && (
            <Section title="Groups / Teams">
              {results.groups.map((g) => (
                <Item
                  key={g?._id}
                  primary={g?.name}
                  secondary={`${g.members.length} members`}
                  onClick={() =>
                    goTo(`/admin/group-manager`, { groupId: g._id })
                  }
                />
              ))}
            </Section>
          )}

          {results.tags?.length > 0 && (
            <Section title="Tags / Skills">
              {results.tags.map((t) => (
                <Item
                  key={t?._id}
                  primary={`#${t?.name}`}
                  secondary={`${t?.usageCount} uses`}
                  onClick={() => goTo(`/admin/tag-manager`)}
                />
              ))}
            </Section>
          )}

          {!results.users?.length &&
            !results.posts?.length &&
            !results.groups?.length &&
            !results.tags?.length && (
              <div className="empty">No results found</div>
            )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
